import { useEffect } from 'react';
import { useSosAlarm } from '@/hooks/useSosAlarm';
import { useAlarmAudio } from '@/hooks/useAlarmAudio';
import SosAlarmModal from './SosAlarmModal';

const TITLE_FLASH_MS = 1000;

/**
 * Flash the tab title while an SOS is unacknowledged, so a console parked in
 * a background tab (common on EOC wall displays) still shows it in the tab
 * strip. Restores the original title on acknowledgement or unmount.
 */
function useTitleFlash(count: number): void {
  const active = count > 0;
  useEffect(() => {
    if (!active) return;
    const original = document.title;
    let on = false;
    const id = setInterval(() => {
      on = !on;
      document.title = on ? `SOS ALARM (${count}) — ${original}` : original;
    }, TITLE_FLASH_MS);
    return () => {
      clearInterval(id);
      document.title = original;
    };
  }, [active, count]);
}

/**
 * App-shell SOS alarm (P0-14). Mounted once per console shell (MainLayout,
 * CallCenterLayout) so every authenticated EOC page raises an SOS — not just
 * the Command and Safety dashboards that used to show a 5-second toast.
 *
 * Renders nothing until an SOS is open. It always mounts, though, because
 * mounting is what arms alarm audio on the operator's first click.
 */
export default function SosAlarmListener() {
  const { alarms, acknowledge, socketConnected, pollingEnabled, pollFailing, canViewLocation } =
    useSosAlarm();
  const { audioArmed, armAudio } = useAlarmAudio(alarms.length > 0);
  useTitleFlash(alarms.length);

  return (
    <SosAlarmModal
      alarms={alarms}
      onAcknowledge={acknowledge}
      audioArmed={audioArmed}
      onEnableAudio={armAudio}
      socketConnected={socketConnected}
      pollingEnabled={pollingEnabled}
      pollFailing={pollFailing}
      canViewLocation={canViewLocation}
    />
  );
}
